import { useState, useEffect, useCallback } from "react";
import type { ThemeMode } from "../types";
import { THEME_STORAGE_KEY } from "../../../../theme/applyTheme";

function getSystemTheme(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function getStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

function resolveTheme(themeMode: ThemeMode): boolean {
  if (themeMode === "system") {
    return getSystemTheme();
  }
  return themeMode === "dark";
}

export function useThemePreference() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(getStoredTheme);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => resolveTheme(getStoredTheme()));

  useEffect(() => {
    setIsDarkMode(resolveTheme(themeMode));
    localStorage.setItem(THEME_STORAGE_KEY, themeMode);
  }, [themeMode]);

  useEffect(() => {
    if (themeMode !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themeMode]);

  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeMode(newTheme);
  }, []);

  return {
    themeMode,
    isDarkMode,
    setTheme,
  };
}
